import React, { useEffect, useMemo, useState, useCallback } from "react";
import TopNav from "@/components/TopNav";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import PlayerForm from "@/components/PlayerForm";
import StatusBadge from "@/components/StatusBadge";
import LogoUploader from "@/components/LogoUploader";
import { toast } from "sonner";
import { ExternalLink, Plus, Trash2, RotateCcw, BedDouble, LogOut as LogOutIcon, Pencil, Loader2 } from "lucide-react";
import BulkFlagDetail from "@/components/BulkFlagDetail";
import CheckCell from "@/components/CheckCell";
import LicenseCell from "@/components/LicenseCell";
import PlayerEditDialog from "@/components/PlayerEditDialog";
import { calcAge } from "@/components/ApprovalCriteria";
import { Progress } from "@/components/ui/progress";
import { useConfirm } from "@/lib/confirm";

export default function Dashboard() {
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [selectedMatchId, setSelectedMatchId] = useState("");
  const [loading, setLoading] = useState(true);
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [teamEditing, setTeamEditing] = useState(null);
  const [bulkFlags, setBulkFlags] = useState({});
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, elapsed: 0 });
  const [flagDetail, setFlagDetail] = useState(null);
  const [editPlayer, setEditPlayer] = useState(null);
  const confirm = useConfirm();

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, mRes] = await Promise.all([api.get("/teams"), api.get("/matches")]);
      setTeams(tRes.data);
      setMatches(mRes.data);
      if (!selectedTeamId && tRes.data[0]) setSelectedTeamId(tRes.data[0].id);
      if (!selectedMatchId && mRes.data[0]) setSelectedMatchId(mRes.data[0].id);
    } catch (e) {
      toast.error("Kunne ikke laste data");
    } finally {
      setLoading(false);
    }
  }, [selectedTeamId, selectedMatchId]);

  const loadPlayers = useCallback(async () => {
    if (!selectedTeamId || !selectedMatchId) { setPlayers([]); return; }
    try {
      const res = await api.get("/players", { params: { team_id: selectedTeamId, match_id: selectedMatchId } });
      setPlayers(res.data);
    } catch (e) {
      setPlayers([]);
    }
  }, [selectedTeamId, selectedMatchId]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadPlayers(); }, [loadPlayers]);

  const currentTeam = useMemo(() => teams.find((t) => t.id === selectedTeamId), [teams, selectedTeamId]);
  const currentMatch = useMemo(() => matches.find((m) => m.id === selectedMatchId), [matches, selectedMatchId]);

  useEffect(() => {
    document.documentElement.style.setProperty("--team-accent", currentTeam?.accent_color || "#09090B");
  }, [currentTeam]);

  const setPlayerStatus = async (player, status) => {
    try {
      await api.post(`/players/${player.id}/status`, { status });
      toast.success(status === "sick" ? "Frafall: syk" : status === "withdrawn" ? "Frafall registrert" : "Status oppdatert");
      loadPlayers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Feil");
    }
  };

  const deletePlayer = async (player) => {
    if (!(await confirm({ title: `Slette ${player.full_name}?`, description: "Spilleren fjernes permanent fra denne påmeldingen.", confirmLabel: "Slett", destructive: true }))) return;
    try {
      await api.delete(`/players/${player.id}`);
      toast.success("Slettet");
      loadPlayers();
    } catch (e) {
      toast.error("Kunne ikke slette");
    }
  };

  const runBulkCheck = async () => {
    if (players.length === 0) return;
    setBulkLoading(true);
    const names = [...new Set(players.map((p) => p.full_name))];
    setBulkProgress({ done: 0, total: names.length, elapsed: 0 });
    const startedAt = Date.now();
    const tick = setInterval(() => {
      const elapsed = (Date.now() - startedAt) / 1000;
      const fraction = Math.min(0.95, elapsed / 8);
      setBulkProgress((p) => ({ ...p, done: Math.floor(fraction * p.total), elapsed }));
    }, 200);
    try {
      const res = await api.post("/lookup/players/bulk", { names });
      setBulkFlags(res.data);
      const hits = Object.values(res.data).filter((v) => v.hits > 0).length;
      if (hits === 0) toast.success(`Sjekket ${names.length} spillere — ingen nivåvarsler`);
      else toast.warning(`${hits} spiller${hits === 1 ? "" : "e"} flagget med NB Nivå`);
    } catch (e) {
      toast.error("Bulk-sjekk feilet — prøv igjen");
    } finally {
      clearInterval(tick);
      setBulkProgress({ done: names.length, total: names.length, elapsed: (Date.now() - startedAt) / 1000 });
      setBulkLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <TopNav />

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        {/* Team context header */}
        <div className="swiss-card overflow-hidden mb-8">
          <div className="accent-bar h-1.5" />
          <div className="p-6 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden">
                {currentTeam?.logo_path ? (
                  <img src={fileUrl(currentTeam.logo_path)} alt={currentTeam.name} className="w-full h-full object-contain" />
                ) : (
                  <span className="font-heading text-2xl font-black text-neutral-400">{currentTeam?.name?.[0] || "?"}</span>
                )}
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-neutral-500">Lagleder</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tighter">{currentTeam?.name || "Velg lag"}</h1>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 items-end">
              <div className="min-w-[200px]">
                <Label className="text-xs uppercase tracking-widest">Lag</Label>
                <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
                  <SelectTrigger data-testid="select-active-team" className="rounded-sm"><SelectValue placeholder="Velg lag" /></SelectTrigger>
                  <SelectContent>
                    {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="new-team-btn" variant="outline" className="rounded-sm" onClick={() => { setTeamEditing(null); setTeamDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-1" />Nytt lag
              </Button>
              {currentTeam && (
                <Button variant="outline" className="rounded-sm" onClick={() => { setTeamEditing(currentTeam); setTeamDialogOpen(true); }} data-testid="edit-team-btn">
                  <Pencil className="w-4 h-4 mr-1" />Rediger lag
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Match selector */}
        <div className="swiss-card p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-end gap-4 justify-between">
            <div className="md:max-w-md w-full">
              <div className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-2">Kamp</div>
              <Select value={selectedMatchId} onValueChange={setSelectedMatchId}>
                <SelectTrigger data-testid="select-active-match" className="rounded-sm h-12 text-base"><SelectValue placeholder="Velg kamp" /></SelectTrigger>
                <SelectContent>
                  {matches.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.title} — {new Date(m.match_date).toLocaleString("nb-NO", { dateStyle: "short", timeStyle: "short" })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {currentMatch && (
                <div className="mt-3 text-sm text-neutral-600">
                  <strong>{new Date(currentMatch.match_date).toLocaleDateString("nb-NO", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</strong>
                  {currentMatch.venue ? ` · ${currentMatch.venue}` : ""}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <PlayerForm
                teams={teams}
                matches={matches}
                defaultTeamId={selectedTeamId}
                defaultMatchId={selectedMatchId}
                onCreated={() => loadPlayers()}
              />
            </div>
          </div>
        </div>

        {/* Roster */}
        <div className="swiss-card">
          <div className="p-6 border-b border-neutral-200 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-neutral-500">Spillerstall</div>
              <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">{players.length} spillere</h2>
            </div>
            <div className="flex items-center gap-3">
              <Button data-testid="bulk-check-btn" onClick={runBulkCheck} disabled={bulkLoading || players.length === 0} className="bg-black text-white hover:bg-neutral-800 rounded-sm font-heading uppercase tracking-widest text-xs">
                {bulkLoading ? (<><Loader2 className="w-3 h-3 mr-2 animate-spin" />Sjekker mot hockey.no…</>) : `Sjekk alle (${players.length}) mot hockey.no`}
              </Button>
              <div className="flex gap-2 text-xs">
                <StatusBadge status="approved" />
                <StatusBadge status="pending" />
                <StatusBadge status="rejected" />
              </div>
            </div>
          </div>

          {bulkLoading && (
            <div className="px-6 py-4 border-b border-neutral-200" data-testid="bulk-progress">
              <div className="flex items-center justify-between text-xs text-neutral-600 mb-2">
                <span className="uppercase tracking-widest font-heading">Sjekker mot live.hockey.no</span>
                <span className="font-mono">{bulkProgress.done}/{bulkProgress.total} · {bulkProgress.elapsed.toFixed(1)}s</span>
              </div>
              <Progress value={bulkProgress.total ? (bulkProgress.done / bulkProgress.total) * 100 : 0} className="h-2" />
              <div className="text-xs text-neutral-500 mt-2">Henter sesongdata fra Eliteserien (kan ta 5-10 sek første gang, deretter raskt).</div>
            </div>
          )}

          {players.length === 0 ? (
            <div className="p-12 text-center">
              <div className="font-heading uppercase tracking-widest text-neutral-400">Ingen spillere meldt på enda</div>
              <div className="text-sm text-neutral-500 mt-2">Bruk "Meld på spiller" for å starte.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="roster-table">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr className="text-left text-xs uppercase tracking-widest text-neutral-500">
                    <th className="px-6 py-3 w-16 text-center">Nr</th>
                    <th className="px-4 py-3">Navn</th>
                    <th className="px-4 py-3">Posisjon</th>
                    <th className="px-4 py-3">E-post</th>
                    <th className="px-4 py-3">Lisens</th>
                    <th className="px-4 py-3">Lisensstatus</th>
                    <th className="px-4 py-3">Sjekk</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Handling</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map((p) => (
                    <tr key={p.id} className="border-b border-neutral-100 hover:bg-neutral-50 align-top">
                      <td className="px-6 py-4 text-center font-heading text-2xl font-black">{p.jersey_number}</td>
                      <td className="px-4 py-4">
                        <div className="font-semibold">{p.full_name}</div>
                        {p.date_of_birth && (() => {
                          const age = calcAge(p.date_of_birth);
                          return (
                            <div className="text-xs text-neutral-500">
                              f. {p.date_of_birth} {age !== null && <span className={age < 16 ? "text-rose-700 font-semibold" : ""}>({age} år{age < 16 ? " – under 16!" : ""})</span>}
                            </div>
                          );
                        })()}
                        {p.rejection_reason && <div className="text-xs text-rose-700 mt-1">Avvist: {p.rejection_reason}</div>}
                      </td>
                      <td className="px-4 py-4">{p.position}</td>
                      <td className="px-4 py-4 text-neutral-600">{p.email}</td>
                      <td className="px-4 py-4 font-mono text-xs">{p.nihf_license}</td>
                      <td className="px-4 py-4">
                        <LicenseCell player={p} isAdmin={false} onChanged={loadPlayers} />
                      </td>
                      <td className="px-4 py-4">
                        <CheckCell player={p} flag={bulkFlags[p.full_name]} onShowDetail={setFlagDetail} />
                      </td>
                      <td className="px-4 py-4"><StatusBadge status={p.status} /></td>
                      <td className="px-4 py-4">
                        <div className="flex justify-end gap-1">
                          <Button data-testid={`edit-${p.id}`} size="sm" variant="ghost" onClick={() => setEditPlayer(p)} title="Rediger">
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {p.status !== "sick" && (
                            <Button data-testid={`mark-sick-${p.id}`} size="sm" variant="ghost" onClick={() => setPlayerStatus(p, "sick")} title="Meld syk">
                              <BedDouble className="w-4 h-4" />
                            </Button>
                          )}
                          {p.status !== "withdrawn" && (
                            <Button data-testid={`mark-withdrawn-${p.id}`} size="sm" variant="ghost" onClick={() => setPlayerStatus(p, "withdrawn")} title="Meld frafall">
                              <LogOutIcon className="w-4 h-4" />
                            </Button>
                          )}
                          {(p.status === "sick" || p.status === "withdrawn") && (
                            <Button data-testid={`restore-${p.id}`} size="sm" variant="ghost" onClick={() => setPlayerStatus(p, "pending")} title="Sett aktiv igjen">
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          )}
                          <Button data-testid={`delete-${p.id}`} size="sm" variant="ghost" onClick={() => deletePlayer(p)} title="Slett">
                            <Trash2 className="w-4 h-4 text-rose-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <TeamDialog open={teamDialogOpen} onOpenChange={setTeamDialogOpen} team={teamEditing} onSaved={(t) => { loadAll(); setSelectedTeamId(t.id); }} />
      <BulkFlagDetail
        open={!!flagDetail}
        onOpenChange={(v) => !v && setFlagDetail(null)}
        playerName={flagDetail?.name}
        flag={flagDetail?.flag}
      />
      <PlayerEditDialog
        open={!!editPlayer}
        onOpenChange={(v) => !v && setEditPlayer(null)}
        player={editPlayer}
        onSaved={() => loadPlayers()}
      />
    </div>
  );
}

function TeamDialog({ open, onOpenChange, team, onSaved }) {
  const [name, setName] = useState("");
  const [accent, setAccent] = useState("#09090B");
  const [logo, setLogo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setName(team?.name || "");
    setAccent(team?.accent_color || "#09090B");
    setLogo(team?.logo_path || null);
  }, [team, open]);

  const save = async () => {
    if (!name.trim()) { toast.error("Skriv inn lagnavn"); return; }
    setSubmitting(true);
    try {
      let res;
      if (team) {
        res = await api.patch(`/teams/${team.id}`, { name, accent_color: accent, logo_path: logo });
      } else {
        res = await api.post("/teams", { name, accent_color: accent, logo_path: logo });
      }
      toast.success(team ? "Lag oppdatert" : "Lag opprettet");
      onSaved(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error("Feil ved lagring");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight text-2xl">{team ? "Rediger lag" : "Nytt lag"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest">Lagnavn</Label>
            <Input data-testid="team-name-input" value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Lagfarge (HEX)</Label>
            <div className="flex items-center gap-3">
              <Input data-testid="team-color-input" type="color" value={accent} onChange={(e) => setAccent(e.target.value)} className="w-16 h-10 p-1 rounded-sm" />
              <Input value={accent} onChange={(e) => setAccent(e.target.value)} className="rounded-sm" />
            </div>
          </div>
          <LogoUploader value={logo} onChange={setLogo} label="Lag-logo" testid="team-logo" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">Avbryt</Button>
          <Button data-testid="save-team-btn" disabled={submitting} onClick={save} className="bg-black text-white hover:bg-neutral-800 rounded-sm font-heading uppercase tracking-widest">
            {submitting ? "Lagrer…" : "Lagre"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
